import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Divider,
} from "@mui/material";
import { StyledCard } from "../common/StyledCard";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import WalletIcon from "@mui/icons-material/AccountBalanceWallet";
import StarIcon from "@mui/icons-material/Star";
import { formatCurrency } from "../../utils/currencyUtils";
import StatusChip from "../common/StatusChip";
import { CustomerSummaryDto } from "../../shared/dtos/customer/CustomerSummaryDto";

interface CustomerCardProps {
  customer: CustomerSummaryDto;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const CustomerCard: React.FC<CustomerCardProps> = ({
  customer,
  onView,
  onEdit,
  onDelete,
}) => {
  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          flexGrow: 1,
        }}
      >
        {/* Header Section: Fixed height/structure to prevent shifting */}
        <Box sx={{ minHeight: 60 }}>
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
          >
            <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", lineHeight: 1.2 }}
                noWrap
              >
                {customer.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: "block" }}
              >
                {customer.email || "No email address"}
              </Typography>
            </Box>
            <StatusChip status={customer.isActive ? "ACTIVE" : "INACTIVE"} />
          </Stack>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Body Section: flexGrow pushes the footer down */}
        <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2">{customer.primaryContact}</Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <WalletIcon fontSize="small" color="secondary" />
            <Typography variant="body2">
              Limit:{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {formatCurrency(customer.creditLimit)}
              </Box>
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <StarIcon fontSize="small" sx={{ color: "#ffc107" }} />
            <Typography variant="body2">
              Loyalty:{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {customer.loyaltyPoints} Pts
              </Box>
            </Typography>
          </Stack>
        </Stack>

        {/* Footer Section: Actions are now pinned to the bottom */}
        <Box sx={{ mt: 2 }}>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ justifyContent: "flex-end" }}
          >
            <Tooltip title="View">
              <IconButton size="small" onClick={onView} color="info">
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit} color="secondary">
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={onDelete} color="error">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Box>
    </StyledCard>
  );
};

export default CustomerCard;
